/**
 * Copyright (c) 2025 Lauft App Inc. All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 */
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Upload, User, Mail, Lock, Home, Search, Check, Phone, Plus, X, Calendar, HelpCircle, ChevronLeft, ChevronRight } from 'react-feather';
import AuthService from '../../services/authService';
import propertyService from '../../services/propertyService';
import icon from '../../assets/dark-iconp.png';

const API_URL = 'https://back-end-x7tx.onrender.com/api';

// Same Cloudinary target the admin QR uploader uses.
const CLOUDINARY_URL = 'https://api.cloudinary.com/v1_1/dckv4itgt/image/upload';
const CLOUDINARY_PRESET = 'lauft_profiles';

/**
 * AGENT ONBOARDING MODAL
 *
 * Forced guided setup, shown over the portal whenever the agent's onboarding
 * isn't complete. There is deliberately NO close button, no backdrop-dismiss,
 * and no escape key — the agent finishes or they don't use the portal. That's
 * also why there's no `onClose` prop: nothing can call one.
 *
 * Styling follows BuyerDetailsModal: white card, #e5e7eb border, 16px radius,
 * translucent blurred scrim, portalled to document.body so it can't get trapped
 * in a stacking context.
 *
 * ADDING A STEP:
 *   1. add the key to ONBOARDING_STEPS in the backend's utils/agentOnboarding.js
 *   2. add a backend endpoint that calls advanceOnboarding(...) with that key
 *   3. add a `case` to renderStep() below
 * The backend's `nextStep` drives which one renders — this component never
 * decides the order itself.
 */
const AgentOnboardingModal = ({ onboarding, onStepComplete, onAllComplete }) => {
  const fileInputRef = useRef(null);

  const [preview, setPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);

  // Listings review step
  const [listings, setListings] = useState(null);   // null = not loaded yet
  const [isLoadingListings, setIsLoadingListings] = useState(false);

  // Invite step
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', phone: '' });
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState([]);   // names we've successfully reached out to
  const [inviteError, setInviteError] = useState('');
  // The add-a-user form lives in its own modal ON TOP of this one. Expanding the
  // onboarding modal to fit an inline form made it lurch in height, which looked
  // broken — a nested sheet keeps this modal a fixed size.
  const [isAddOpen, setIsAddOpen] = useState(false);

  // Branding step (confirm_info). Prefilled from whatever admin already set,
  // which on a fresh account is usually nothing — that's the point of the step.
  const [branding, setBranding] = useState({
    agency: '',
    licenseNumber: '',
    aliasEmail: ''
  });
  const [brandingHydrated, setBrandingHydrated] = useState(false);
  // Alias email is opt-in. Most agents don't want one, and an empty input
  // sitting there invites them to wonder whether they're supposed to fill it.
  const [showAlias, setShowAlias] = useState(false);
  // Toggled, not hover-only — the PWA has no hover and "alias email" is exactly
  // the term an agent on their phone won't recognise.
  const [showAliasTip, setShowAliasTip] = useState(false);

  // Pool selection step
  const [poolData, setPoolData] = useState(null);      // null = not loaded yet
  const [isLoadingPools, setIsLoadingPools] = useState(false);
  const [countySearch, setCountySearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [selectedPresets, setSelectedPresets] = useState([]);
  const [selectedPools, setSelectedPools] = useState([]);

  // Which step the agent is LOOKING at. null = follow the server's nextStep,
  // which is the normal forward flow. Set only when they navigate back to
  // review something they've already done.
  //
  // The server still owns what's complete and what's next — this is a view
  // offset on top of that, never a substitute for it.
  const [viewStep, setViewStep] = useState(null);

  // The branding step goes two-column on desktop and stacks on the PWA. Inline
  // style objects can't hold a media query, so we track the breakpoint instead.
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 700px)').matches
  );

  // NOTE: no early return up here. React requires every hook to run in the same
  // order on every render, and useEffect (the listings fetch) lives below — so the
  // "nothing to show" guard has to sit AFTER all hooks, just before the render.
  // Everything between here and there must tolerate a null `onboarding`.
  const completed = onboarding?.completedSteps || [];
  const activeStep = viewStep || onboarding?.nextStep;

  // Reviewing = looking at something already finished. Drives the banner, hides
  // the skip link, and neuters the two steps whose re-submit would destroy data.
  const isReviewing = !!viewStep && completed.includes(viewStep);

  // Steps they can navigate back into: completed, and something this component
  // can actually draw. 'password' is completed for everyone here but happens on
  // the emailed link, so it has no case and must stay out.
  const REVISITABLE = ['profile_photo', 'confirm_info', 'review_listings', 'select_pools', 'invite_users'];
  const revisitable = (onboarding?.steps || []).filter(
    step => completed.includes(step) && REVISITABLE.includes(step)
  );

  // Position of what they're looking at, not how far they've got — otherwise
  // stepping back to step 2 still reads "Step 5 of 7".
  const activeIndex = (onboarding?.steps || []).indexOf(activeStep);
  const stepNumber = activeIndex >= 0 ? activeIndex + 1 : completed.length + 1;
  const totalSteps = onboarding?.steps?.length || 1;

  const goToStep = (step) => {
    setError('');
    setViewStep(step);
  };

  // Return to wherever the server says they actually are.
  const resumeCurrent = () => {
    setError('');
    setViewStep(null);
  };

  const handleFilePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');

    if (!file.type.startsWith('image/')) {
      setError('That file isn\'t an image. Pick a JPG or PNG.');
      return;
    }

    // Cloudinary would take more, but a 5MB ceiling keeps the upload quick on
    // the mobile PWA, where most agents will hit this.
    if (file.size > 5 * 1024 * 1024) {
      setError('That image is over 5MB. Pick a smaller one.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => setPreview({ url: ev.target.result, file });
    reader.readAsDataURL(file);
  };

  const handleUploadPhoto = async () => {
    if (!preview?.file) return;

    setIsUploading(true);
    setError('');

    try {
      // Straight to Cloudinary from the client, same as the admin QR uploader.
      const formData = new FormData();
      formData.append('file', preview.file);
      formData.append('upload_preset', CLOUDINARY_PRESET);
      formData.append('folder', 'lauft/agent-profiles');

      const cloudinaryResponse = await fetch(CLOUDINARY_URL, {
        method: 'POST',
        body: formData
      });

      if (!cloudinaryResponse.ok) {
        throw new Error('Upload failed. Try again.');
      }

      const cloudinaryData = await cloudinaryResponse.json();
      const imageUrl = cloudinaryData.secure_url;

      const response = await AuthService.fetchWithAuth(
        `${API_URL}/auth/onboarding/profile-photo`,
        {
          method: 'PUT',
          body: JSON.stringify({ profileImage: imageUrl })
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Could not save your photo.');
      }

      // Keep the cached user in sync so the sidebar avatar updates without a
      // reload — otherwise localStorage still holds the old profileImage.
      const cached = AuthService.getCurrentUser();
      if (cached) {
        localStorage.setItem(
          'user_data',
          JSON.stringify({ ...cached, profileImage: imageUrl })
        );
      }

      finishStep(data.data);
    } catch (err) {
      setError(err.message);
      setIsUploading(false);
    }
  };

  // Pull the agent's listings when (and only when) they land on that step.
  //
  // ACTIVE ONLY. AgentPropertyList renders approvalStatus 'approved' as "Active"
  // and 'pending' as "In Review", so we just don't call getPendingProperties —
  // there's nothing for an agent to sign off on in a listing that isn't live yet.
  const isListingsStep = activeStep === 'review_listings';
  const agentId = onboarding?.agentId;

  useEffect(() => {
    if (!isListingsStep || !agentId) return;

    let cancelled = false;
    setIsLoadingListings(true);

    const fetchListings = async () => {
      try {
        const params = { agent: agentId, page: 1, limit: 50 };

        const approvedRes = await propertyService.getAgentProperties(params);

        const active = (approvedRes?.data || [])
          .map(p => ({ ...p, approvalStatus: 'approved' }))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        if (!cancelled) setListings(active);
      } catch (err) {
        console.error('Error fetching listings for onboarding:', err);
        // Fail soft: an empty list still lets them finish. Blocking onboarding on
        // a listings fetch would be a bad trade.
        if (!cancelled) setListings([]);
      } finally {
        if (!cancelled) setIsLoadingListings(false);
      }
    };

    fetchListings();
    return () => { cancelled = true; };
  }, [isListingsStep, agentId]);

  // Seed the branding inputs ONCE. `onboarding` gets replaced wholesale by every
  // step-complete response, so an unguarded sync would wipe whatever the agent
  // was mid-way through typing.
  useEffect(() => {
    if (brandingHydrated || !onboarding) return;
    setBranding({
      agency: onboarding.agency || '',
      licenseNumber: onboarding.licenseNumber || '',
      aliasEmail: onboarding.aliasEmail || ''
    });
    // Admin may have set one already — don't hide an existing value behind a
    // button that reads like it would create a second one.
    setShowAlias(!!onboarding.aliasEmail);
    setBrandingHydrated(true);
  }, [onboarding, brandingHydrated]);

  // Load the agent's counties + pools when they land on that step.
  //
  // Fails SOFT, same call as the listings step: an empty list still lets them
  // finish. Blocking onboarding on this fetch would trap them behind a modal
  // with no close button.
  const isPoolsStep = activeStep === 'select_pools';

  useEffect(() => {
    if (!isPoolsStep) return;

    let cancelled = false;
    setIsLoadingPools(true);

    const fetchPools = async () => {
      try {
        const response = await AuthService.fetchWithAuth(
          `${API_URL}/auth/onboarding/pools`
        );
        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Could not load markets.');
        }

        if (cancelled) return;

        const d = data.data;
        setPoolData(d);

        // Re-check what they asked for last time, so coming back to this step
        // shows their actual selection instead of a blank slate. Filtered to
        // what's still on offer — anything already approved has been dropped
        // from `pools` server-side.
        const presetIds = d.requestedPresetIds || [];
        const coveredByPresets = new Set(
          (d.counties || [])
            .filter(c => presetIds.includes(String(c._id)))
            .flatMap(c => c.poolIds)
        );
        const visible = new Set((d.pools || []).map(p => String(p._id)));

        setSelectedPresets(presetIds);
        setSelectedPools(
          (d.requestedPoolIds || []).filter(
            id => visible.has(id) && !coveredByPresets.has(id)
          )
        );
      } catch (err) {
        console.error('Error fetching onboarding pools:', err);
        if (!cancelled) {
          setPoolData({ territory: null, state: null, counties: [], pools: [] });
        }
      } finally {
        if (!cancelled) setIsLoadingPools(false);
      }
    };

    fetchPools();
    return () => { cancelled = true; };
  }, [isPoolsStep]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 700px)');
    const onChange = (e) => setIsNarrow(e.matches);
    // addListener is the deprecated spelling, but it's the only one older iOS
    // Safari has — and the PWA is exactly where this matters.
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  /**
   * Finish a review step (confirm_info or review_listings).
   *
   * `requestedChanges` advances the step either way — see the backend comment on
   * confirmOnboardingInfo. A mailto is fire-and-forget, so we can't know whether
   * the agent actually sent it, and holding them behind the modal until someone
   * hand-edits their record would be a support ticket, not a safeguard.
   */
  const handleReviewStep = async (endpoint, requestedChanges, mailtoBuilder) => {
    setIsConfirming(true);
    setError('');

    if (requestedChanges && mailtoBuilder) {
      window.open(mailtoBuilder(), '_self');
    }

    try {
      const response = await AuthService.fetchWithAuth(
        `${API_URL}/auth/onboarding/${endpoint}`,
        {
          method: 'PUT',
          body: JSON.stringify({ requestedChanges })
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Could not save. Try again.');
      }

      finishStep(data.data);
    } catch (err) {
      setError(err.message);
      setIsConfirming(false);
    }
  };

  /**
   * Every step ends here. Clearing viewStep matters: saving from a step they'd
   * navigated back into should return them to where they actually are, not
   * strand them in the past looking at a stale screen.
   */
  const finishStep = (data) => {
    setViewStep(null);
    if (data.complete) {
      onAllComplete(data);
    } else {
      onStepComplete(data);
    }
  };

  const toggleInList = (setter) => (id) =>
    setter(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const togglePreset = toggleInList(setSelectedPresets);
  const togglePool = toggleInList(setSelectedPools);

  /**
   * Save the pool step.
   *
   * Sends preset ids and pool ids separately — the server expands counties
   * itself rather than trusting an expanded list from us, and tags the resulting
   * requests with which county they came through so admin can approve a county
   * as one row instead of twenty-three.
   *
   * Zero selections is a valid submit. Some markets have no counties or pools
   * configured at all, and the step still has to let them through.
   */
  const handleSavePools = async () => {
    setIsConfirming(true);
    setError('');

    try {
      const response = await AuthService.fetchWithAuth(
        `${API_URL}/auth/onboarding/pools`,
        {
          method: 'PUT',
          body: JSON.stringify({
            presetIds: selectedPresets,
            poolIds: selectedPools
          })
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Could not save. Try again.');
      }

      finishStep(data.data);
    } catch (err) {
      setError(err.message);
      setIsConfirming(false);
    }
  };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /**
   * Save the branding step.
   *
   * Unlike review_listings this one WRITES — the agent is supplying agency,
   * licenseNumber and (optionally) aliasEmail, not signing off on them. The
   * backend merges via dot-paths so plan/qrCodeUrl/precautionMode on agentInfo
   * survive untouched.
   *
   * Name, login email and phone stay admin-owned and read-only; the mailto
   * below is still the only way to change those.
   */
  const handleSaveBranding = async () => {
    const agency = branding.agency.trim();
    const licenseNumber = branding.licenseNumber.trim();
    // Collapsed means no alias, whatever is left in state.
    const aliasEmail = showAlias ? branding.aliasEmail.trim() : '';

    setError('');

    if (!agency) {
      setError('Enter your brokerage name.');
      return;
    }
    if (!licenseNumber) {
      setError('Enter your license number.');
      return;
    }
    if (aliasEmail && !EMAIL_RE.test(aliasEmail)) {
      setError("That alias email doesn't look right.");
      return;
    }

    setIsConfirming(true);

    try {
      const response = await AuthService.fetchWithAuth(
        `${API_URL}/auth/onboarding/confirm-info`,
        {
          method: 'PUT',
          body: JSON.stringify({
            agency,
            licenseNumber,
            // null, not '' — the schema default is null and the displayEmail
            // virtual falls back to the real email on a falsy alias.
            aliasEmail: aliasEmail || null
          })
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Could not save. Try again.');
      }

      // Keep the cached user in sync so the portal picks up the new brokerage
      // and alias without a reload, same as the profile photo step does.
      const cached = AuthService.getCurrentUser();
      if (cached) {
        localStorage.setItem(
          'user_data',
          JSON.stringify({
            ...cached,
            agentInfo: {
              ...(cached.agentInfo || {}),
              agency,
              licenseNumber,
              aliasEmail: aliasEmail || null
            }
          })
        );
      }

      finishStep(data.data);
    } catch (err) {
      setError(err.message);
      setIsConfirming(false);
    }
  };

  // Same template as AgentDashboard's "Request Information Update" button.
  const buildInfoMailto = () => {
    const subject = encodeURIComponent('Request to Update Agent Information');
    const body = encodeURIComponent(
      `Hello,\n\nI would like to request an update to my agent information:\n\n` +
      `Current Information:\n` +
      `Agent Name: ${onboarding.name}\n` +
      `Agent ID: ${onboarding.agentId}\n` +
      `Email: ${onboarding.email}\n` +
      `Phone: ${onboarding.phone || 'Not provided'}\n` +
      `Brokerage: ${onboarding.agency || 'Not provided'}\n` +
      `License Number: ${onboarding.licenseNumber || 'Not provided'}\n\n` +
      `Requested Changes:\n` +
      `Name: [Your New Name]\n` +
      `Email: [Your New Email]\n` +
      `Phone: [Your New Phone]\n` +
      `Brokerage: [Your New Brokerage]\n` +
      `License Number: [Your New License Number]\n\n` +
      `Thank you.`
    );
    return `mailto:team@lauft.app?subject=${subject}&body=${body}`;
  };

  // Listing problems go to the same inbox, different subject so the team can
  // triage which kind of request it is.
  const buildListingsMailto = () => {
    const lines = (listings || [])
      .map(p => `- ${p.address || 'Unknown address'}`)
      .join('\n');

    const subject = encodeURIComponent('Request to Update Agent Listings');
    const body = encodeURIComponent(
      `Hello,\n\nThere's an issue with the listings on my account:\n\n` +
      `Agent Name: ${onboarding.name}\n` +
      `Agent ID: ${onboarding.agentId}\n` +
      `Email: ${onboarding.email}\n\n` +
      `Current Active Listings (${(listings || []).length}):\n` +
      `${lines || 'None'}\n\n` +
      `What's wrong:\n` +
      `[Missing listings / listings that aren't mine / incorrect details]\n\n` +
      `Thank you.`
    );
    return `mailto:team@lauft.app?subject=${subject}&body=${body}`;
  };

  /**
   * Send one invite.
   *
   * FRAMING vs REALITY: the UI says "search and connect" — look the person up,
   * send a connection request if they're on Lauft, an invite if they're not. The
   * lookup doesn't exist yet, so this always hits POST /api/invitations and always
   * sends an invite.
   *
   * The success copy says "Connection request sent" in both cases. Until the
   * lookup exists that is literally always an invite email, so the wording runs
   * ahead of the behaviour — a deliberate product call, not an oversight.
   */
  const handleSendInvite = async () => {
    const name = inviteForm.name.trim();
    const email = inviteForm.email.trim();
    const phone = inviteForm.phone.trim();

    setInviteError('');

    if (!name) {
      setInviteError('Enter their name.');
      return;
    }
    if (!email) {
      setInviteError('Enter their email — we need it to reach them.');
      return;
    }

    setIsSending(true);

    try {
      // Same endpoint + payload as AgentInvitations' send form.
      const response = await fetch(`${API_URL}/invitations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          recipientName: name,
          recipientEmail: email || undefined,
          recipientPhone: phone || undefined,
          message: ''
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Could not reach them. Try again.');
      }

      setSent(prev => [...prev, name]);
      setInviteForm({ name: '', email: '', phone: '' });
      setIsAddOpen(false);
    } catch (err) {
      setInviteError(err.message);
    } finally {
      setIsSending(false);
    }
  };

  const handleFinishInvites = async (skipped) => {
    setIsConfirming(true);
    setError('');

    try {
      const response = await AuthService.fetchWithAuth(
        `${API_URL}/auth/onboarding/invite-users`,
        {
          method: 'PUT',
          body: JSON.stringify({ invitedCount: sent.length, skipped })
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Could not save. Try again.');
      }

      finishStep(data.data);
    } catch (err) {
      setError(err.message);
      setIsConfirming(false);
    }
  };

  /**
   * Skip the current step.
   *
   * Skipping still advances — the server records the key in `skippedSteps` so a
   * pass-through is distinguishable from a real completion, but the agent isn't
   * shown the step again. Anything less and "skip" would be a lie.
   */
  const handleSkipStep = async () => {
    setIsConfirming(true);
    setError('');

    try {
      const response = await AuthService.fetchWithAuth(
        `${API_URL}/auth/onboarding/skip`,
        {
          method: 'PUT',
          body: JSON.stringify({ step: activeStep })
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Could not skip. Try again.');
      }

      finishStep(data.data);
    } catch (err) {
      setError(err.message);
      setIsConfirming(false);
    }
  };

  // Every step in the modal gets one. (The password step isn't in here — it
  // happens on the emailed link, and it's the one thing that can't be skipped.)
  const skipLink = (label = 'Skip for now') => (
    // Nothing to skip on a step they've already finished.
    isReviewing ? null : (
    <button
      onClick={handleSkipStep}
      disabled={isConfirming || isSending || isUploading}
      style={{
        ...styles.skipButton,
        cursor: isConfirming || isSending || isUploading ? 'not-allowed' : 'pointer',
        opacity: isConfirming || isSending || isUploading ? 0.5 : 1,
      }}
    >
      {label}
    </button>
    )
  );

  const infoRow = (label, value) => (
    <div style={styles.infoRow}>
      <span style={styles.infoLabel}>{label}</span>
      <span style={{
        ...styles.infoValue,
        color: value ? '#111' : '#9ca3af',
        fontStyle: value ? 'normal' : 'italic',
      }}>
        {value || 'Not provided'}
      </span>
    </div>
  );

  /**
   * Shared label for the alias field — used by both the collapsed and expanded
   * states, so the explanation is in the same place either way.
   *
   * The trigger is a real button so it's keyboard-reachable, which means it can
   * end up nested inside the <label>. preventDefault stops the label from
   * bouncing focus into the input every time someone opens the tooltip.
   */
  const aliasLabel = (
    <>
      Alias email
      <span style={styles.optionalTag}>Optional</span>
      <span style={styles.tipWrap}>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowAliasTip(v => !v);
          }}
          onMouseEnter={() => setShowAliasTip(true)}
          onMouseLeave={() => setShowAliasTip(false)}
          aria-label="What is an alias email?"
          aria-expanded={showAliasTip}
          style={styles.tipTrigger}
        >
          <HelpCircle size={13} />
        </button>

        {showAliasTip && (
          <span role="tooltip" style={styles.tipBubble}>
            An alias email is a second address users see on your profile and in
            messages — useful if you'd rather hand out a work address than the
            one your account runs on. It doesn't change how you sign in, and we
            still send account email to your login address.
          </span>
        )}
      </span>
    </>
  );

  const renderStep = () => {
    switch (activeStep) {
      case 'profile_photo':
        return (
          <>
            <div style={styles.header}>
              <div>
                <h2 style={styles.title}>Add a profile photo</h2>
                <p style={styles.subtitle}>
                  Users see this when you claim and message them. A clear headshot
                  gets more responses than a logo.
                </p>
              </div>
            </div>

            <div style={styles.content}>
              <div style={styles.uploadArea}>
                <div
                  onClick={() => !isUploading && fileInputRef.current?.click()}
                  style={{
                    ...styles.avatarWell,
                    cursor: isUploading ? 'not-allowed' : 'pointer',
                    backgroundImage: preview ? `url(${preview.url})` : 'none',
                  }}
                >
                  {!preview && (
                    <div style={styles.avatarPlaceholder}>
                      <User size={26} strokeWidth={1.5} />
                    </div>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFilePick}
                  style={{ display: 'none' }}
                />

                <div style={styles.uploadMeta}>
                  <button
                    onClick={() => !isUploading && fileInputRef.current?.click()}
                    disabled={isUploading}
                    style={styles.secondaryButton}
                  >
                    <Upload size={14} />
                    {preview ? 'Replace photo' : 'Choose photo'}
                  </button>
                  <p style={styles.uploadHint}>JPG or PNG, up to 5MB.</p>
                </div>
              </div>

              {error && <div style={styles.error}>{error}</div>}

              <button
                onClick={isReviewing && !preview ? resumeCurrent : handleUploadPhoto}
                // While reviewing, "no new photo picked" isn't an error state —
                // they came to look at it. The button becomes the way out
                // instead of a dead control.
                disabled={isUploading || (!preview && !isReviewing)}
                onMouseEnter={(e) => {
                  if (preview && !isUploading) {
                    e.currentTarget.style.background = '#f0f0f0';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#fafafa';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
                style={{
                  ...styles.primaryButton,
                  opacity: isUploading || (!preview && !isReviewing) ? 0.45 : 1,
                  cursor: isUploading || (!preview && !isReviewing) ? 'not-allowed' : 'pointer',
                }}
              >
                {isUploading
                  ? 'Uploading...'
                  : preview
                    ? (isReviewing ? 'Save new photo' : 'Next')
                    : isReviewing ? 'Back to where you left off' : 'Next'}
              </button>

              {skipLink()}
            </div>
          </>
        );

      case 'confirm_info':
        return (
          <>
            <div style={styles.header}>
              <div>
                <h2 style={styles.title}>Confirm your agent branding</h2>
                <p style={styles.subtitle}>
                  This is the information that users see when you claim and
                  message them. Your brokerage and license appear on your
                  profile.
                </p>
              </div>
            </div>

            <div style={styles.content}>
              {/* Locked details on the left, the fields they actually fill on
                  the right. Stacked this was the tallest step in the flow and
                  ran off short viewports. */}
              <div style={{
                ...styles.twoCol,
                flexDirection: isNarrow ? 'column' : 'row',
              }}>
                {/* Admin-owned. Still read-only — changing these is a mailto. */}
                <div style={{ ...styles.infoCard, ...styles.col }}>
                  {infoRow('Name', onboarding.name)}
                  {infoRow('Email', onboarding.email)}
                  {infoRow('Phone', onboarding.phone)}

                  <div style={styles.lockNote}>
                    <Lock size={12} />
                    <span>Only our team can change these.</span>
                  </div>
                </div>

                {/* Agent-owned. These are the ones the step exists to collect. */}
                <div style={{ ...styles.fieldStack, ...styles.col }}>
                  <div style={styles.field}>
                    <label style={styles.fieldLabel} htmlFor="lo-agency">
                      Brokerage
                    </label>
                    <input
                      id="lo-agency"
                      type="text"
                      value={branding.agency}
                      onChange={(e) => setBranding(b => ({ ...b, agency: e.target.value }))}
                      placeholder="e.g. Coldwell Banker Realty"
                      disabled={isConfirming}
                      autoComplete="organization"
                      style={styles.textInput}
                    />
                  </div>

                  <div style={styles.field}>
                    <label style={styles.fieldLabel} htmlFor="lo-license">
                      License number
                    </label>
                    <input
                      id="lo-license"
                      type="text"
                      value={branding.licenseNumber}
                      onChange={(e) => setBranding(b => ({ ...b, licenseNumber: e.target.value }))}
                      placeholder="e.g. 01234567"
                      disabled={isConfirming}
                      autoComplete="off"
                      style={styles.textInput}
                    />
                  </div>

                  {/* The label row renders in both states so the tooltip is
                      reachable BEFORE they commit to adding one — that's the
                      moment "alias email" needs explaining. */}
                  <div style={styles.field}>
                    <div style={styles.fieldLabelRow}>
                      {showAlias ? (
                        <label style={styles.fieldLabel} htmlFor="lo-alias">
                          {aliasLabel}
                        </label>
                      ) : (
                        <span style={styles.fieldLabel}>{aliasLabel}</span>
                      )}

                      {showAlias && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowAlias(false);
                            setShowAliasTip(false);
                            setBranding(b => ({ ...b, aliasEmail: '' }));
                            setError('');
                          }}
                          disabled={isConfirming}
                          style={{
                            ...styles.fieldRemove,
                            cursor: isConfirming ? 'not-allowed' : 'pointer',
                          }}
                        >
                          <X size={13} />
                          Remove
                        </button>
                      )}
                    </div>

                    {showAlias ? (
                      <>
                        <input
                          id="lo-alias"
                          type="email"
                          value={branding.aliasEmail}
                          onChange={(e) => setBranding(b => ({ ...b, aliasEmail: e.target.value }))}
                          placeholder="you@brokerage.com"
                          disabled={isConfirming}
                          autoComplete="email"
                          autoFocus
                          style={styles.textInput}
                        />
                        {/* The alias only changes what users SEE. Worth saying
                            plainly — an agent who thinks this is their login
                            will lock themselves out of the portal in their own
                            head. */}
                        <p style={styles.fieldHint}>
                          Users see this instead of your login email. Sign-in
                          doesn't change.
                        </p>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowAlias(true)}
                        disabled={isConfirming}
                        onMouseEnter={(e) => {
                          if (!isConfirming) e.currentTarget.style.borderColor = '#d1d5db';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                        }}
                        style={{
                          ...styles.addAliasButton,
                          cursor: isConfirming ? 'not-allowed' : 'pointer',
                          opacity: isConfirming ? 0.5 : 1,
                        }}
                      >
                        <Plus size={14} />
                        Add alias email
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {error && <div style={styles.error}>{error}</div>}

              <button
                onClick={handleSaveBranding}
                disabled={isConfirming}
                onMouseEnter={(e) => {
                  if (!isConfirming) {
                    e.currentTarget.style.background = '#f0f0f0';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#fafafa';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
                style={{
                  ...styles.primaryButton,
                  opacity: isConfirming ? 0.45 : 1,
                  cursor: isConfirming ? 'not-allowed' : 'pointer',
                }}
              >
                {isConfirming ? 'Saving...' : 'Save and continue'}
              </button>

              <button
                onClick={() => handleReviewStep('confirm-info', true, buildInfoMailto)}
                disabled={isConfirming}
                style={{
                  ...styles.textButton,
                  cursor: isConfirming ? 'not-allowed' : 'pointer',
                }}
              >
                <Mail size={14} />
                Name, email or phone wrong? Request changes
              </button>

              {skipLink()}
            </div>
          </>
        );

      case 'select_pools': {
        const counties = poolData?.counties || [];
        const allPools = poolData?.pools || [];
        const hasAnything = counties.length > 0 || allPools.length > 0;

        // Pools already covered by a checked county. Shown as covered rather
        // than hidden — a city vanishing from the list the moment you tick a
        // county reads as a bug, and they may be searching for exactly that city
        // to confirm it's included.
        const covered = new Set(
          counties
            .filter(c => selectedPresets.includes(c._id))
            .flatMap(c => c.poolIds)
        );

        const cq = countySearch.trim().toLowerCase();
        const visibleCounties = cq
          ? counties.filter(c => c.name.toLowerCase().includes(cq))
          : counties;

        const pq = citySearch.trim().toLowerCase();
        const visiblePools = (pq
          ? allPools.filter(p => {
              const city = (p.serviceArea?.city || '').toLowerCase();
              return p.name.toLowerCase().includes(pq) || city.includes(pq);
            })
          : allPools
        )
          // Their own market first — otherwise a San Antonio agent scrolls past
          // Addison to reach anything relevant.
          .slice()
          .sort((a, b) => (b.inMarket === true) - (a.inMarket === true));

        const totalPools = new Set([
          ...covered,
          ...selectedPools
        ]).size;

        return (
          <>
            <div style={styles.header}>
              <div>
                <h2 style={styles.title}>Choose the areas you work</h2>
                <p style={styles.subtitle}>
                  Pick whole counties, or add individual cities. We'll go through
                  these together on your onboarding call before anything goes live.
                </p>
              </div>
            </div>

            <div style={styles.content}>
              {isLoadingPools ? (
                <div style={styles.listingsLoading}>Loading your areas...</div>
              ) : !hasAnything ? (
                <div style={styles.emptyListings}>
                  <p style={styles.emptyTitle}>Nothing to choose yet</p>
                  <p style={styles.emptyBody}>
                    We haven't set up areas for your market. We'll sort this out
                    on your onboarding call — carry on for now.
                  </p>
                </div>
              ) : (
                <>
                  {counties.length > 0 && (
                    <div style={styles.pickerBlock}>
                      <div style={styles.pickerLabelRow}>
                        <span style={styles.fieldLabel}>Counties</span>
                        <span style={styles.pickerCount}>
                          {selectedPresets.length} selected
                        </span>
                      </div>

                      <div style={styles.searchWrap}>
                        <Search size={13} style={styles.searchIcon} />
                        <input
                          type="text"
                          value={countySearch}
                          onChange={(e) => setCountySearch(e.target.value)}
                          placeholder="Search counties"
                          disabled={isConfirming}
                          style={styles.searchInput}
                        />
                      </div>

                      <div style={styles.pickerList}>
                        {visibleCounties.length === 0 ? (
                          <p style={styles.pickerEmpty}>No counties match that.</p>
                        ) : visibleCounties.map(county => {
                          const isSel = selectedPresets.includes(county._id);
                          return (
                            <button
                              key={county._id}
                              type="button"
                              onClick={() => togglePreset(county._id)}
                              disabled={isConfirming}
                              style={{
                                ...styles.pickerRow,
                                ...(isSel ? styles.pickerRowSelected : {}),
                                cursor: isConfirming ? 'not-allowed' : 'pointer',
                              }}
                            >
                              <span style={styles.pickerCheck(isSel)}>
                                {isSel && <Check size={11} color="#fff" strokeWidth={3} />}
                              </span>
                              <span style={styles.pickerName}>{county.name}</span>
                              <span style={styles.pickerMeta}>
                                {county.poolCount} {county.poolCount === 1 ? 'area' : 'areas'}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {allPools.length > 0 && (
                    <div style={styles.pickerBlock}>
                      <div style={styles.pickerLabelRow}>
                        <span style={styles.fieldLabel}>
                          Individual cities
                          <span style={styles.optionalTag}>Optional</span>
                        </span>
                        <span style={styles.pickerCount}>
                          {selectedPools.length} selected
                        </span>
                      </div>

                      <div style={styles.searchWrap}>
                        <Search size={13} style={styles.searchIcon} />
                        <input
                          type="text"
                          value={citySearch}
                          onChange={(e) => setCitySearch(e.target.value)}
                          placeholder="Search cities"
                          disabled={isConfirming}
                          style={styles.searchInput}
                        />
                      </div>

                      <div style={styles.pickerList}>
                        {visiblePools.length === 0 ? (
                          <p style={styles.pickerEmpty}>No cities match that.</p>
                        ) : visiblePools.map(pool => {
                          const isCovered = covered.has(String(pool._id));
                          const isSel = isCovered || selectedPools.includes(pool._id);
                          return (
                            <button
                              key={pool._id}
                              type="button"
                              onClick={() => !isCovered && togglePool(pool._id)}
                              disabled={isConfirming || isCovered}
                              style={{
                                ...styles.pickerRow,
                                ...(isSel ? styles.pickerRowSelected : {}),
                                cursor: isConfirming || isCovered ? 'default' : 'pointer',
                                opacity: isCovered ? 0.65 : 1,
                              }}
                            >
                              <span style={styles.pickerCheck(isSel)}>
                                {isSel && <Check size={11} color="#fff" strokeWidth={3} />}
                              </span>
                              <span style={styles.pickerName}>{pool.name}</span>
                              {isCovered && (
                                <span style={styles.pickerMeta}>In a county you picked</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                </>
              )}

              {error && <div style={styles.error}>{error}</div>}

              {/* Sticky: the running total and the way forward stay on screen
                  while they scroll the lists above. */}
              <div style={styles.poolFooter}>
                {hasAnything && (
                  <div style={styles.poolSummary}>
                    {totalPools === 0
                      ? "Nothing selected yet. We can also add these togther on your onboarding call."
                      : `${totalPools} ${totalPools === 1 ? 'area' : 'areas'} selected.`}
                  </div>
                )}

                <button
                  onClick={handleSavePools}
                  disabled={isConfirming || isLoadingPools}
                  onMouseEnter={(e) => {
                    if (!isConfirming && !isLoadingPools) {
                      e.currentTarget.style.background = '#f0f0f0';
                      e.currentTarget.style.borderColor = '#d1d5db';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#fafafa';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                  style={{
                    ...styles.primaryButton,
                    opacity: isConfirming || isLoadingPools ? 0.45 : 1,
                    cursor: isConfirming || isLoadingPools ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isConfirming
                    ? 'Saving...'
                    : totalPools > 0 ? 'Save and continue' : 'Continue'}
                </button>

                {skipLink()}
              </div>
            </div>
          </>
        );
      }

      case 'review_listings': {
        const count = listings?.length || 0;
        const hasListings = count > 0;

        return (
          <>
            <div style={styles.header}>
              <div>
                <h2 style={styles.title}>Review your listings</h2>
                <p style={styles.subtitle}>
                  {hasListings
                    ? "These are the active listings on your account. Check that they're all yours and nothing's missing."
                    : "We haven't synced any active listings to your account yet."}
                </p>
              </div>
            </div>

            <div style={styles.content}>
              {isLoadingListings ? (
                <div style={styles.listingsLoading}>Loading your listings...</div>
              ) : !hasListings ? (
                <div style={styles.emptyListings}>
                  <p style={styles.emptyTitle}>No active listings</p>
                  <p style={styles.emptyBody}>
                    You can add listings any time from My Listings. If you expected
                    to see some here, let us know.
                  </p>
                </div>
              ) : (
                <div style={styles.listingsList}>
                  {listings.map((property) => (
                    <div key={property._id} style={styles.listingRow}>
                      {property.mainImage ? (
                        <img
                          src={property.mainImage}
                          alt={property.address}
                          style={styles.listingThumb}
                        />
                      ) : (
                        <div style={{ ...styles.listingThumb, ...styles.listingThumbEmpty }}>
                          <Home size={16} strokeWidth={1.5} />
                        </div>
                      )}

                      <div style={styles.listingMeta}>
                        <span style={styles.listingAddress}>
                          {property.address || 'Unknown address'}
                        </span>
                        <span style={styles.listingCsz}>{property.csz || ''}</span>
                      </div>

                    </div>
                  ))}
                </div>
              )}

              {error && <div style={styles.error}>{error}</div>}

              <button
                onClick={() => handleReviewStep('review-listings', false)}
                disabled={isConfirming || isLoadingListings}
                onMouseEnter={(e) => {
                  if (!isConfirming && !isLoadingListings) {
                    e.currentTarget.style.background = '#f0f0f0';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#fafafa';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
                style={{
                  ...styles.primaryButton,
                  opacity: isConfirming || isLoadingListings ? 0.45 : 1,
                  cursor: isConfirming || isLoadingListings ? 'not-allowed' : 'pointer',
                }}
              >
                {isConfirming
                  ? 'Saving...'
                  : hasListings ? 'Looks right' : 'Continue'}
              </button>

              <button
                onClick={() => handleReviewStep('review-listings', true, buildListingsMailto)}
                disabled={isConfirming || isLoadingListings}
                style={{
                  ...styles.textButton,
                  cursor: isConfirming || isLoadingListings ? 'not-allowed' : 'pointer',
                }}
              >
                <Mail size={14} />
                {hasListings
                  ? "Something's wrong — request changes"
                  : 'I should have listings — let us know'}
              </button>

              {skipLink()}
            </div>
          </>
        );
      }

      case 'invite_users': {
        // Two slots by default — two visible holes read as "you're meant to fill
        // these" in a way a blank form never does. Grows past two if they want.
        const slotCount = Math.max(2, sent.length + (sent.length >= 2 ? 1 : 0));
        const slots = Array.from({ length: slotCount }, (_, i) => sent[i] || null);

        return (
          <>
            <div style={styles.header}>
              <div>
                <h2 style={styles.title}>Search or invite users</h2>
                <p style={styles.subtitle}>
                  Look up the people you're already working with. If they're on
                  Lauft, we'll send them a connection request. If they're not,
                  we'll send them an invite.
                </p>
              </div>
            </div>

            <div style={styles.content}>
              {/* Stacked, left-aligned rows — same shape as the profile photo field */}
              <div style={styles.slotStack}>
                {slots.map((name, i) => (
                  name ? (
                    <div key={`filled-${i}`} style={styles.slotFilled}>
                      <div style={styles.slotAvatarFilled}>
                        <Check size={17} color="#fff" strokeWidth={3} />
                      </div>
                      <div style={styles.slotMeta}>
                        <span style={styles.slotNameFilled}>{name}</span>
                        <span style={styles.slotStatusFilled}>Connection request sent</span>
                      </div>
                    </div>
                  ) : (
                    <button
                      key={`empty-${i}`}
                      onClick={() => {
                        setInviteError('');
                        setIsAddOpen(true);
                      }}
                      disabled={isConfirming}
                      style={{
                        ...styles.slotEmpty,
                        cursor: isConfirming ? 'not-allowed' : 'pointer',
                        opacity: isConfirming ? 0.6 : 1,
                      }}
                    >
                      <div style={styles.slotAvatarEmpty}>
                        <User size={22} strokeWidth={1.5} />
                      </div>
                      <div style={styles.slotMeta}>
                        <span style={styles.slotLabel}>Add a user</span>
                        <span style={styles.slotHint}>
                          Someone you're already working with
                        </span>
                      </div>
                      <div style={styles.slotPlus}>
                        <Plus size={15} strokeWidth={2.5} />
                      </div>
                    </button>
                  )
                ))}
              </div>

              {/* Why bother. One sentence — the icon list read as clip art. */}
              <div style={styles.benefits}>
                Connecting with a user lets you see their insights, send them
                properties, and tap into their personalized algorithm and feed.
              </div>

              {error && <div style={styles.error}>{error}</div>}

              <button
                // `sent` is local state, so revisiting this step starts empty.
                // Re-submitting would write invitedCount: 0 over whatever they
                // actually did the first time, so while reviewing this is a
                // navigation control, not a save.
                onClick={() => isReviewing ? resumeCurrent() : handleFinishInvites(false)}
                disabled={isConfirming || (!isReviewing && sent.length === 0)}
                onMouseEnter={(e) => {
                  if (!isConfirming && sent.length > 0) {
                    e.currentTarget.style.background = '#f0f0f0';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#fafafa';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
                style={{
                  ...styles.primaryButton,
                  opacity: isConfirming || (!isReviewing && sent.length === 0) ? 0.45 : 1,
                  cursor: isConfirming || (!isReviewing && sent.length === 0) ? 'not-allowed' : 'pointer',
                }}
              >
                {isConfirming
                  ? 'Finishing...'
                  : isReviewing ? 'Back to where you left off' : 'Done'}
              </button>

              {skipLink(sent.length > 0 ? 'Finish later' : 'Skip for now')}
            </div>
          </>
        );
      }

      case 'onboarding_call': {
        // THE WALL.
        //
        // The agent has done everything they can; the last step is admin-only and
        // gets marked during the live onboarding call. Deliberately has no skip,
        // no primary action, and nothing to click into the portal — that is the
        // entire point. It should read as "you're ahead of schedule", not as an
        // error, which is why the finished checklist sits above the message.
        const stepLabels = {
          password: 'Password created',
          profile_photo: 'Profile photo added',
          confirm_info: 'Details confirmed',
          review_listings: 'Listings reviewed',
          select_pools: 'Areas selected',
          invite_users: 'Clients invited'
        };

        // ORDER COMES FROM THE SERVER. This maps over onboarding.steps, which is
        // ONBOARDING_STEPS in utils/agentOnboarding.js — so the sequence here is
        // whatever the backend says it is. If this list looks out of order,
        // that's the deployed backend, not this component.

        const finished = (onboarding.steps || [])
          .filter((step) => step !== 'onboarding_call');

        return (
          <>
            <div style={styles.header}>
              <div>
                <h2 style={styles.title}>You're all set for now</h2>
                <p style={styles.subtitle}>
                  Nice work, that's everything we needed from you. We'll finish
                  setting up your account together on your onboarding call.
                </p>
              </div>
            </div>

            <div style={styles.content}>
              <div style={styles.wallList}>
                {finished.map((step) => (
                  <div key={step} style={styles.wallRow}>
                    <div style={styles.wallCheck}>
                      <Check size={11} color="#fff" strokeWidth={4} />
                    </div>
                    <span style={styles.wallLabel}>
                      {stepLabels[step] || step}
                    </span>
                  </div>
                ))}
              </div>

              <div style={styles.wallNote}>
                <Calendar size={15} style={styles.wallNoteIcon} />
                <span>
                  Your account opens up on your onboarding call. We'll walk through
                  your territory, claiming users, and getting your first
                  conversations going.
                </span>
              </div>

              {/* Not an escape hatch into the portal, just a way off a shared
                  machine. Remove if you'd rather they have nothing at all. */}
              <button
                onClick={() => {
                  AuthService.logout();
                  window.location.href = '/';
                }}
                style={styles.wallSignOut}
              >
                Sign out
              </button>
            </div>
          </>
        );
      }

      default:
        // Unreachable — the RENDERABLE_STEPS guard above returns null before we
        // get here. Kept as a belt-and-braces null rather than the old "Almost
        // there / Refresh" screen, which was the thing that trapped agents:
        // refreshing didn't change nextStep, so the button did nothing forever.
        return null;
    }
  };

  // Nothing to show: no status yet (the fetch failed or hasn't landed), or the
  // agent is done. Safe here — every hook above has already run.
  if (!onboarding || onboarding.status === 'complete') return null;

  // ---- NEVER TRAP THE AGENT ----
  // This modal can only render steps it has a case for. If nextStep is something
  // else, showing the modal pins the agent behind a screen they cannot clear —
  // which is exactly what happened to every pre-existing agent when 'password'
  // (handled on the emailed link, not here) came back as their next step.
  //
  // A missed onboarding step is a nuisance. A locked-out agent is an outage.
  // When in doubt, let them in.
  //
  // KEEP THIS IN SYNC WITH ONBOARDING_STEPS. The guard cuts both ways: a step
  // missing from this list doesn't just skip that step, it dismisses the whole
  // modal — including the onboarding_call wall — so the agent gets a fully
  // unlocked portal. Adding a step to the backend without adding it here is a
  // silent hole in the lock.
  const RENDERABLE_STEPS = [
    'profile_photo',
    'confirm_info',
    'review_listings',
    'select_pools',
    'invite_users',
    'onboarding_call'
  ];
  if (!RENDERABLE_STEPS.includes(activeStep)) return null;

  // A separate sheet stacked on top of the onboarding modal. Its own portal +
  // higher z-index, so the modal underneath keeps its size and doesn't jump.
  const addUserModal = isAddOpen ? createPortal(
    <div
      style={styles.addOverlay}
      onClick={() => !isSending && setIsAddOpen(false)}
    >
      <div style={styles.addModal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.addHeader}>
          <div>
            <h3 style={styles.addTitle}>Add a user</h3>
            <p style={styles.addSubtitle}>
              We'll look them up and either connect or invite them.
            </p>
          </div>
          <button
            onClick={() => !isSending && setIsAddOpen(false)}
            disabled={isSending}
            style={{
              ...styles.addClose,
              cursor: isSending ? 'not-allowed' : 'pointer',
            }}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div style={styles.addBody}>
          <div style={styles.inviteInputWrap}>
            <User size={15} style={styles.inviteIcon} />
            <input
              type="text"
              value={inviteForm.name}
              onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
              placeholder="Full name"
              disabled={isSending}
              autoFocus
              style={styles.inviteInput}
            />
          </div>

          <div style={styles.inviteInputWrap}>
            <Mail size={15} style={styles.inviteIcon} />
            <input
              type="email"
              value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              placeholder="Email"
              disabled={isSending}
              style={styles.inviteInput}
            />
          </div>

          <div style={styles.inviteInputWrap}>
            <Phone size={15} style={styles.inviteIcon} />
            <input
              type="tel"
              value={inviteForm.phone}
              onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
              placeholder="Phone (optional)"
              disabled={isSending}
              style={styles.inviteInput}
            />
          </div>

          {inviteError && <div style={styles.error}>{inviteError}</div>}

          <button
            onClick={handleSendInvite}
            disabled={isSending}
            style={{
              ...styles.slotSendButton,
              cursor: isSending ? 'not-allowed' : 'pointer',
              opacity: isSending ? 0.5 : 1,
            }}
          >
            <Search size={14} />
            {isSending ? 'Sending...' : 'Connect and invite'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return createPortal(
    <>
    {addUserModal}
    <div style={styles.overlay}>
      <div style={{
        ...styles.modal,
        // The branding step runs two columns; every other step is a single
        // narrow column and looks stretched at this width.
        maxWidth: activeStep === 'confirm_info' ? '760px' : '560px',
      }}>
        {/* Flow header. Frames the whole setup, not the individual step — the
            step gets its own header below the divider. */}
        <div style={styles.modalHeader}>
          <div style={styles.brand}>
            <img src={icon} alt="Lauft" style={styles.brandIcon} />
            <h2 style={styles.modalTitle}>Getting started</h2>
          </div>

          {totalSteps > 1 && (
            <div style={styles.progressCluster}>
              <div style={styles.pips}>
                {onboarding.steps.map((step) => {
                  const isDone = completed.includes(step);
                  const isCurrent = step === activeStep;
                  // Only finished, drawable steps are clickable. Forward jumps
                  // stay impossible — the server decides what's next.
                  const canVisit = revisitable.includes(step) && !isCurrent;
                  return (
                    <div
                      key={step}
                      onClick={() => canVisit && !isConfirming && goToStep(step)}
                      title={canVisit ? 'Review this step' : undefined}
                      style={{
                        ...styles.pip,
                        cursor: canVisit && !isConfirming ? 'pointer' : 'default',
                        background: isDone
                          ? '#2563eb'
                          : isCurrent
                            ? '#111'
                            : '#e5e7eb',
                      }}
                    />
                  );
                })}
              </div>
              <span style={styles.progressLabel}>
                Step {stepNumber} of {totalSteps}
              </span>
            </div>
          )}
        </div>

        {/* Back control. Sits above the step so it reads as chrome, not as one
            of the step's own actions. Hidden on the first revisitable step and
            while a save is in flight. */}
        {(() => {
          const idx = revisitable.indexOf(activeStep);
          // Where "back" goes: the previous revisitable step, or — if they're on
          // the current (unfinished) step — the last one they finished.
          const target = idx > 0
            ? revisitable[idx - 1]
            : idx === -1
              ? revisitable[revisitable.length - 1]
              : null;

          // Forward one completed step. Null on the last one — from there the
          // only way forward is back to the step they're actually on, which the
          // resume button already does. Two controls doing the same thing side
          // by side would just be a decision to make for no reason.
          const nextTarget =
            idx >= 0 && idx < revisitable.length - 1 ? revisitable[idx + 1] : null;

          // Bar renders if ANY of its controls do. Gating the whole thing on the
          // back target alone hid Next and the resume link on the first
          // revisitable step, where back is legitimately null.
          if (isConfirming || (!target && !isReviewing)) return null;

          return (
            <div style={styles.navBar}>
              {target ? (
                <button
                  type="button"
                  onClick={() => goToStep(target)}
                  style={styles.navBack}
                >
                  <ChevronLeft size={14} />
                  Back
                </button>
              ) : (
                // Holds the left edge so space-between keeps the pair on the right.
                <span />
              )}

              {isReviewing && (
                <div style={styles.navRight}>
                  {nextTarget && (
                    <button
                      type="button"
                      onClick={() => goToStep(nextTarget)}
                      style={styles.navNext}
                    >
                      Next
                      <ChevronRight size={14} />
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={resumeCurrent}
                    style={styles.navResume}
                  >
                    Back to where you left off
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {isReviewing && (
          <div style={styles.reviewNote}>
            You've already finished this step. Changes you make here will be saved.
          </div>
        )}

        {renderStep()}
      </div>
    </div>
    </>,
    document.body
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999999,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    width: '100%',
    border: '1px solid #e5e7eb',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.18)',
    // Was `overflow: hidden` with no ceiling, so a tall step didn't scroll —
    // it just got cut off by the viewport. maxWidth is per-step now, applied
    // at the call site.
    maxHeight: 'calc(100vh - 40px)',
    overflowX: 'hidden',
    overflowY: 'auto',
  },
  pips: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  /* Small bars, not check-filled circles. Circles-with-ticks read like a game
     progress tracker; bars read like software. */
  pip: {
    width: '22px',
    height: '3px',
    borderRadius: '2px',
    transition: 'background 0.2s ease',
  },
  progressLabel: {
    fontSize: '0.75rem',
    color: '#9ca3af',
    fontWeight: '500',
  },
  /* ---- Flow header: persists across every step ---- */
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '20px',
    padding: '18px 28px',
    borderBottom: '1px solid #f0f0f0',
    background: '#fafafa',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '11px',
    minWidth: 0,
  },
  brandIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    flexShrink: 0,
    display: 'block',
  },
  modalTitle: {
    fontSize: '1.4rem',
    fontWeight: '700',
    margin: 0,
    color: '#111',
    textAlign: 'left',
    letterSpacing: '-0.01em',
  },
  progressCluster: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexShrink: 0,
  },

  /* ---- Step header: secondary to the flow header above it ---- */
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '26px 28px 0 28px',
  },
  title: {
    fontSize: '1.1rem',
    fontWeight: '700',
    margin: 0,
    color: '#111',
    textAlign: 'left',
    letterSpacing: '-0.01em',
  },
  subtitle: {
    fontSize: '0.85rem',
    color: '#6b7280',
    margin: '6px 0 0 0',
    lineHeight: 1.6,
  },
  content: {
    padding: '22px 28px 28px 28px',
  },
  /* Left-aligned row, not a centered column — reads as a form field rather
     than a splash screen. */
  uploadArea: {
    display: 'flex',
    alignItems: 'center',
    gap: '18px',
    padding: '16px',
    marginBottom: '20px',
    borderRadius: '10px',
    border: '1px solid #f0f0f0',
    background: '#fafafa',
  },
  /* Solid hairline, NOT dashed. Dashed borders are the strongest "wireframe"
     signal in a UI — they make a finished product look like a mockup. */
  avatarWell: {
    width: '84px',
    height: '84px',
    borderRadius: '50%',
    border: '1px solid #e5e7eb',
    backgroundColor: '#ffffff',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'border-color 0.15s ease',
  },
  avatarPlaceholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#c0c4cb',
  },
  uploadMeta: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '8px',
    minWidth: 0,
  },
  uploadHint: {
    margin: 0,
    fontSize: '0.75rem',
    color: '#9ca3af',
  },
  secondaryButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    padding: '7px 12px',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
    background: '#ffffff',
    color: '#374151',
    fontSize: '0.8rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  primaryButton: {
    width: '100%',
    padding: '13px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    background: '#fafafa',
    color: '#111',
    fontSize: '0.92rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease',
  },
  infoCard: {
    borderRadius: '10px',
    border: '1px solid #f0f0f0',
    background: '#fafafa',
    padding: '4px 16px',
    marginBottom: '20px',
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    padding: '13px 0',
    borderBottom: '1px solid #f0f0f0',
  },
  infoLabel: {
    fontSize: '0.8rem',
    color: '#6b7280',
    fontWeight: '500',
    flexShrink: 0,
  },
  infoValue: {
    fontSize: '0.87rem',
    fontWeight: '600',
    textAlign: 'right',
    wordBreak: 'break-word',
  },
  lockNote: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '11px 0',
    fontSize: '0.73rem',
    color: '#9ca3af',
  },
  /* ---- Back navigation chrome ---- */
  navBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '10px 28px 0 28px',
  },
  navBack: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 6px 4px 0',
    border: 'none',
    background: 'none',
    color: '#6b7280',
    fontSize: '0.8rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'color 0.15s ease',
  },
  navRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
  },
  /* Mirrors navBack, chevron trailing. Grey like Back — these are navigation,
     and shouldn't compete with the step's own primary action. */
  navNext: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 0',
    border: 'none',
    background: 'none',
    color: '#6b7280',
    fontSize: '0.8rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'color 0.15s ease',
  },
  navResume: {
    padding: '4px 0',
    border: 'none',
    background: 'none',
    color: '#2563eb',
    fontSize: '0.78rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  reviewNote: {
    margin: '10px 28px 0 28px',
    padding: '9px 12px',
    borderRadius: '8px',
    background: 'rgba(37, 99, 235, 0.05)',
    border: '1px solid rgba(37, 99, 235, 0.16)',
    color: '#1e40af',
    fontSize: '0.78rem',
    lineHeight: 1.5,
  },

  /* ---- select_pools: county + city pickers ---- */
  pickerBlock: {
    marginBottom: '14px',
  },
  /* Pinned to the bottom of the modal's scrollport so "Save and continue" is
     reachable without scrolling past two lists to find it. Negative margins
     span the content padding so rows scroll under it edge to edge. */
  poolFooter: {
    position: 'sticky',
    bottom: 0,
    margin: '0 -28px -28px -28px',
    padding: '12px 28px 20px 28px',
    background: '#ffffff',
    borderTop: '1px solid #f0f0f0',
  },
  pickerLabelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    marginBottom: '7px',
  },
  pickerCount: {
    fontSize: '0.75rem',
    fontWeight: '600',
    color: '#9ca3af',
  },
  searchWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
  },
  searchIcon: {
    position: 'absolute',
    left: '11px',
    color: '#9ca3af',
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '9px 12px 9px 32px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    background: '#ffffff',
    color: '#111',
    fontSize: '0.85rem',
    outline: 'none',
  },
  /* Capped and scrollable. Kept deliberately short: with two of these stacked
     plus their search boxes, anything taller pushes the primary button below
     the fold. The sticky footer below is the belt to this pair of braces. */
  pickerList: {
    maxHeight: '124px',
    overflowY: 'auto',
    borderRadius: '10px',
    border: '1px solid #f0f0f0',
    background: '#fafafa',
    padding: '5px',
  },
  pickerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    width: '100%',
    padding: '8px 9px',
    borderRadius: '7px',
    border: '1px solid transparent',
    background: 'transparent',
    textAlign: 'left',
    transition: 'background 0.12s ease, border-color 0.12s ease',
  },
  pickerRowSelected: {
    background: 'rgba(37, 99, 235, 0.06)',
    borderColor: 'rgba(37, 99, 235, 0.18)',
  },
  pickerCheck: (isSel) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    borderRadius: '5px',
    flexShrink: 0,
    background: isSel ? '#2563eb' : '#ffffff',
    border: isSel ? '1px solid #2563eb' : '1px solid #d1d5db',
  }),
  pickerName: {
    flex: 1,
    minWidth: 0,
    fontSize: '0.84rem',
    fontWeight: '600',
    color: '#111',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  pickerMeta: {
    fontSize: '0.73rem',
    color: '#9ca3af',
    flexShrink: 0,
  },
  pickerEmpty: {
    margin: 0,
    padding: '14px 9px',
    fontSize: '0.8rem',
    color: '#9ca3af',
  },
  poolSummary: {
    padding: '9px 12px',
    marginBottom: '2px',
    borderRadius: '9px',
    background: '#fafafa',
    border: '1px solid #f0f0f0',
    fontSize: '0.8rem',
    color: '#6b7280',
  },

  /* ---- confirm_info: locked details beside the editable fields ---- */
  twoCol: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '18px',
    marginBottom: '20px',
  },
  /* minWidth:0 or a long email in the info card refuses to shrink and blows
     the row past the modal. */
  col: {
    flex: '1 1 0',
    minWidth: 0,
    marginBottom: 0,
  },
  /* ---- Editable branding fields on confirm_info ---- */
  fieldStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    marginBottom: '20px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  fieldLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    fontSize: '0.78rem',
    fontWeight: '600',
    color: '#374151',
  },
  optionalTag: {
    fontSize: '0.68rem',
    fontWeight: '600',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  textInput: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '11px 12px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    background: '#ffffff',
    color: '#111',
    fontSize: '0.88rem',
    outline: 'none',
  },
  /* Anchor for the tooltip bubble. */
  tipWrap: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
  },
  tipTrigger: {
    display: 'flex',
    alignItems: 'center',
    padding: 0,
    border: 'none',
    background: 'none',
    color: '#9ca3af',
    cursor: 'pointer',
    transition: 'color 0.15s ease',
  },
  /* Opens DOWNWARD. The modal scrolls now, and a bubble above the trigger gets
     clipped whenever this field sits near the top of the scroll area. */
  tipBubble: {
    position: 'absolute',
    top: 'calc(100% + 7px)',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '236px',
    maxWidth: '70vw',
    padding: '10px 12px',
    borderRadius: '8px',
    background: '#111827',
    color: '#f3f4f6',
    fontSize: '0.73rem',
    fontWeight: '400',
    lineHeight: 1.55,
    textAlign: 'left',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.22)',
    zIndex: 5,
    pointerEvents: 'none',
  },
  fieldLabelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
  },
  fieldRemove: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: 0,
    border: 'none',
    background: 'none',
    color: '#9ca3af',
    fontSize: '0.73rem',
    fontWeight: '600',
    transition: 'color 0.15s ease',
  },
  /* Full-width and dashed so the empty slot reads as "add one" rather than as
     a field that failed to render. */
  addAliasButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '7px',
    width: '100%',
    padding: '11px',
    borderRadius: '8px',
    border: '1px dashed #e5e7eb',
    background: '#ffffff',
    color: '#6b7280',
    fontSize: '0.82rem',
    fontWeight: '600',
    transition: 'border-color 0.15s ease, color 0.15s ease',
  },
  fieldHint: {
    margin: '2px 0 0 0',
    fontSize: '0.73rem',
    lineHeight: 1.55,
    color: '#9ca3af',
    wordBreak: 'break-word',
  },
  listingsList: {
    maxHeight: '280px',
    overflowY: 'auto',
    borderRadius: '10px',
    border: '1px solid #f0f0f0',
    background: '#fafafa',
    padding: '4px 12px',
    marginBottom: '20px',
  },
  listingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 0',
    borderBottom: '1px solid #f0f0f0',
  },
  listingThumb: {
    width: '44px',
    height: '44px',
    borderRadius: '7px',
    objectFit: 'cover',
    flexShrink: 0,
    border: '1px solid #e5e7eb',
    background: '#ffffff',
  },
  listingThumbEmpty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#c0c4cb',
  },
  listingMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
    flex: 1,
  },
  listingAddress: {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: '#111',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  listingCsz: {
    fontSize: '0.75rem',
    color: '#9ca3af',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  listingsLoading: {
    padding: '32px',
    textAlign: 'center',
    color: '#9ca3af',
    fontSize: '0.85rem',
    borderRadius: '10px',
    border: '1px solid #f0f0f0',
    background: '#fafafa',
    marginBottom: '20px',
  },
  emptyListings: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '28px 20px',
    borderRadius: '10px',
    border: '1px solid #f0f0f0',
    background: '#fafafa',
    color: '#c0c4cb',
    marginBottom: '20px',
  },
  /* No icon above these any more — the title is the first thing in the box. */
  emptyTitle: {
    margin: '0 0 4px 0',
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#374151',
  },
  emptyBody: {
    margin: 0,
    fontSize: '0.78rem',
    color: '#9ca3af',
    lineHeight: 1.55,
    maxWidth: '300px',
  },
  /* Stacked, left-aligned rows. Deliberately the same shape as the profile
     photo field above: silhouette avatar on the left, meta on the right. */
  slotStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '16px',
  },
  slotEmpty: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    width: '100%',
    textAlign: 'left',
    padding: '14px 16px',
    borderRadius: '10px',
    border: '1px solid #f0f0f0',
    background: '#fafafa',
    transition: 'all 0.15s ease',
  },
  slotAvatarEmpty: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    border: '1px solid #e5e7eb',
    background: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#c0c4cb',
    flexShrink: 0,
  },
  slotFilled: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    width: '100%',
    padding: '14px 16px',
    borderRadius: '10px',
    border: '1px solid rgba(37, 99, 235, 0.22)',
    background: 'rgba(37, 99, 235, 0.05)',
  },
  slotAvatarFilled: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: '#2563eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  slotMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    minWidth: 0,
    flex: 1,
  },
  slotLabel: {
    fontSize: '0.87rem',
    fontWeight: '600',
    color: '#111',
  },
  slotHint: {
    fontSize: '0.75rem',
    color: '#9ca3af',
  },
  slotNameFilled: {
    fontSize: '0.87rem',
    fontWeight: '700',
    color: '#1e40af',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  slotStatusFilled: {
    fontSize: '0.75rem',
    fontWeight: '600',
    color: 'rgba(30, 64, 175, 0.65)',
  },
  slotPlus: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    border: '1px solid #e5e7eb',
    background: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6b7280',
    flexShrink: 0,
  },
  slotSendButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '7px',
    width: '100%',
    padding: '11px',
    borderRadius: '8px',
    border: '1px solid #2563eb',
    background: '#2563eb',
    color: '#ffffff',
    fontSize: '0.85rem',
    fontWeight: '600',
    transition: 'opacity 0.15s ease',
  },

  /* ---- Nested add-user sheet, stacked above the onboarding modal ---- */
  addOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    backdropFilter: 'blur(3px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000000,   // one above the onboarding modal's 999999
    padding: '20px',
  },
  addModal: {
    backgroundColor: '#ffffff',
    borderRadius: '14px',
    width: '100%',
    maxWidth: '400px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 24px 70px rgba(0, 0, 0, 0.28)',
    overflow: 'hidden',
  },
  addHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '18px 20px',
    borderBottom: '1px solid #f0f0f0',
    background: '#fafafa',
  },
  addTitle: {
    margin: 0,
    fontSize: '1.05rem',
    fontWeight: '700',
    color: '#111',
    letterSpacing: '-0.01em',
  },
  addSubtitle: {
    margin: '4px 0 0 0',
    fontSize: '0.78rem',
    color: '#6b7280',
    lineHeight: 1.5,
  },
  addClose: {
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    padding: 0,
    display: 'flex',
    flexShrink: 0,
    transition: 'color 0.12s',
  },
  addBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '20px',
  },
  /* Same box as the other fields on this step — just prose inside it now
     instead of an icon list, which read as clip art. */
  benefits: {
    padding: '14px 16px',
    borderRadius: '10px',
    border: '1px solid #f0f0f0',
    background: '#fafafa',
    marginBottom: '20px',
    fontSize: '0.82rem',
    lineHeight: 1.65,
    color: '#374151',
  },
  inviteInputWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  inviteIcon: {
    position: 'absolute',
    left: '11px',
    color: '#9ca3af',
    pointerEvents: 'none',
  },
  inviteInput: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 12px 10px 34px',
    borderRadius: '7px',
    border: '1px solid #e5e7eb',
    background: '#ffffff',
    color: '#111',
    fontSize: '0.85rem',
    outline: 'none',
  },
  sentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '20px',
  },
  sentRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    padding: '9px 12px',
    borderRadius: '8px',
    background: 'rgba(37, 99, 235, 0.06)',
    border: '1px solid rgba(37, 99, 235, 0.18)',
  },
  sentCheck: {
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    background: '#2563eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  sentName: {
    fontSize: '0.82rem',
    fontWeight: '600',
    color: '#1e40af',
  },
  inviteFootnote: {
    margin: '12px 0 0 0',
    textAlign: 'center',
    fontSize: '0.75rem',
    color: '#9ca3af',
  },
  wallList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '14px 16px',
    borderRadius: '10px',
    border: '1px solid #f0f0f0',
    background: '#fafafa',
    marginBottom: '16px',
  },
  wallRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '6px 0',
  },
  wallCheck: {
    width: '17px',
    height: '17px',
    borderRadius: '50%',
    background: '#2563eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  wallLabel: {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: '#374151',
  },
  wallNote: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '14px 16px',
    borderRadius: '10px',
    border: '1px solid rgba(37, 99, 235, 0.18)',
    background: 'rgba(37, 99, 235, 0.05)',
    fontSize: '0.82rem',
    lineHeight: 1.6,
    color: '#1e40af',
  },
  wallNoteIcon: {
    color: '#2563eb',
    flexShrink: 0,
    marginTop: '2px',
  },
  wallSignOut: {
    display: 'block',
    width: '100%',
    marginTop: '16px',
    padding: '9px',
    borderRadius: '8px',
    border: 'none',
    background: 'transparent',
    color: '#9ca3af',
    fontSize: '0.8rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'color 0.15s ease',
  },
  skipButton: {
    display: 'block',
    width: '100%',
    marginTop: '10px',
    padding: '9px',
    borderRadius: '8px',
    border: 'none',
    background: 'transparent',
    color: '#9ca3af',
    fontSize: '0.8rem',
    fontWeight: '600',
    transition: 'color 0.15s ease',
  },
  textButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '7px',
    width: '100%',
    marginTop: '10px',
    padding: '10px',
    borderRadius: '8px',
    border: 'none',
    background: 'transparent',
    color: '#6b7280',
    fontSize: '0.82rem',
    fontWeight: '600',
    transition: 'color 0.15s ease',
  },
  error: {
    padding: '10px 12px',
    marginBottom: '16px',
    borderRadius: '6px',
    background: 'rgba(239, 68, 68, 0.08)',
    border: '1px solid rgba(239, 68, 68, 0.2)',
    color: '#dc2626',
    fontSize: '0.82rem',
  },
};

export default AgentOnboardingModal;